/**
 * tutorial.ts — The guided walk, as a pure reducer.
 *
 * Level 1 teaches the game rather than testing it (see
 * `systems/levelgen/tutorial.ts` for the town it does that on). This module is
 * the *script*: an ordered run of lessons covering the whole walk, plus asides
 * that fire when the world produces the thing they are about.
 *
 *   intro → walk → lead → critters → urge → grass → bin → home
 *
 * ## Two kinds of lesson, because a tutorial that only reacts races ahead
 *
 * The first cut of this script had every lesson finished by something the
 * player did, and it blazed: a player who set off briskly cleared "walk" and
 * "lead" in a couple of seconds each and was three cards deep before reading
 * one. Explaining the game needs both:
 *
 * - **A task** is finished by doing it — walking a real distance, getting the
 *   dog on the grass, reaching the bin. `done(facts)`.
 * - **A briefing** has nothing to do and is finished by the clock:
 *   `TUTORIAL.BRIEF_MS`, long enough to read a sentence about what critters do
 *   to a dog. Nothing is blocked while one is up; the walk carries on.
 *
 * Both kinds sit in one ordered list, so "step 4 of 9" still counts something
 * real.
 *
 * ## The script directs the level, rather than only watching it
 *
 * Three things a first walk must contain arrive on their own schedule, which on
 * a teaching level means "possibly never, or possibly all at once":
 *
 * - **The dog's urge.** Held back until the script reaches the lesson about it
 *   (see {@link holdsUrge}), then *hurried* so the lesson pays off in seconds
 *   rather than the usual eight-to-seventeen. Without the hold, the urge landing
 *   mid-briefing would skip every lesson before it — which is exactly what the
 *   `stale` machinery below is for, and exactly what a guided level should not
 *   need to lean on.
 * - **The car at the crossing.** Released when the walker carrying the bag first
 *   approaches a street (`release-vehicle`), so the trip to the bin contains one
 *   car, seen coming, with the horn and the aside to explain it. A scheduled car
 *   would arrive while they were still on the doorstep or not at all.
 * - **The critter to point at.** The level's next scheduled critter, brought
 *   forward to the briefing about wildlife and let in at a distance
 *   (`release-critter`). Not an extra animal — the same one, earlier.
 *
 * All three are {@link TutorialEffect}s, applied by `Game` — the same split as
 * `objectives.ts`, where the reducer decides and the caller does.
 *
 * ## The rules that keep it honest
 *
 * - **Every task is finished by playing.** Nothing asks for anything the walk
 *   does not already require, so a player who ignores the cards still walks the
 *   script end to end.
 * - **A card is held for at least `MIN_SHOW_MS`**, so a task completed
 *   instantly is still readable — *unless* the walk has genuinely left it
 *   behind, which is what `stale` means. "Finished" and "left behind" are
 *   different questions: covering the ground in one frame finishes the lead
 *   lesson, while the dog squatting makes it wrong to still be showing.
 *
 * ## What lives where
 *
 * Ids and rules here; **wording in `ui/hud.ts`**, with every other line the
 * player reads, so a lesson can be re-worded for the control scheme in use the
 * same way the objective line is.
 */
import { TUTORIAL } from '../config/constants';
import { DogState, Objective } from '../types';

/** One lesson in the guided walk. Ordered; the ids are the HUD's copy keys. */
export type TutorialStepId =
  | 'intro'
  | 'walk'
  | 'lead'
  | 'critters'
  | 'urge'
  | 'grass'
  | 'bin'
  | 'home';

/** One aside, shown once when the world produces the thing it is about. */
export type TutorialTipId = 'traffic' | 'critter';

/** What the run looks like this frame, as far as the script cares. */
export interface TutorialFacts {
  /** How far the walker has *walked* — their own steering, not the dog's tug. */
  readonly walkedPx: number;
  readonly dogState: DogState;
  readonly objective: Objective;
  /** The frenzy meter, 0..1 — non-zero only while a critter is pulling. */
  readonly frenzy: number;
  /** Did a vehicle sound its horn at the pair this frame? */
  readonly hornSounding: boolean;
  /** Is the walker close enough to a carriageway to be about to cross it? */
  readonly nearRoad: boolean;
}

/** Where the script has got to. Everything is derived from this. */
export interface TutorialState {
  /** Which lesson is showing. `TUTORIAL_STEPS.length` once the script is done. */
  readonly index: number;
  /** How long the current lesson has been up: the read floor, and the briefings. */
  readonly shownMs: number;
  /** The aside currently on the card, if any. */
  readonly tip: TutorialTipId | null;
  /** How much longer it stays. */
  readonly tipMs: number;
  /** Asides already spent — each one is shown once per run. */
  readonly seenTips: readonly TutorialTipId[];
  /** Directions already given — each one is acted on once per run. */
  readonly spentEffects: readonly TutorialEffect['type'][];
}

/**
 * Something the script wants the level to do. Declarative, like an
 * `ObjectiveEffect`: "a car should come now", not "spawn one at x, y".
 */
export type TutorialEffect =
  /** Bring the dog's urge on, so the lesson about it isn't a long wait. */
  | { readonly type: 'hurry-urge' }
  /** Let a critter in near the pair, so the briefing about them has one in it. */
  | { readonly type: 'release-critter' }
  /** Send a car down the street the walker is about to cross. */
  | { readonly type: 'release-vehicle' };

/** State plus whatever it wants doing. */
export type TutorialStep = readonly [TutorialState, readonly TutorialEffect[]];

/** What the HUD draws: which lesson, how far through, and any aside with it. */
export interface TutorialCard {
  readonly step: TutorialStepId;
  /** 1-based, as the player is shown it. */
  readonly number: number;
  readonly total: number;
  readonly tip: TutorialTipId | null;
}

interface Lesson {
  readonly id: TutorialStepId;
  /**
   * Has the player done this? Omitted for a **briefing** — a card with nothing
   * to do, finished by `TUTORIAL.BRIEF_MS` on the clock instead.
   */
  done?(facts: TutorialFacts): boolean;
  /**
   * Has the walk left this lesson behind — not merely finished it, but moved on
   * to a later phase entirely?
   *
   * The only thing that lifts the read floor. Covering 900 units in one frame
   * *finishes* the lead lesson, and flicking past it unread would be a bug; the
   * dog squatting on the grass makes it wrong to still be showing.
   */
  stale?(facts: TutorialFacts): boolean;
  /** Given once, when this lesson comes up. */
  onShow?: TutorialEffect;
  /** Given once, the first frame this holds while the lesson is up. */
  when?: { readonly is: (facts: TutorialFacts) => boolean; readonly effect: TutorialEffect };
}

/**
 * The objective chain in order, so "has got as far as" is a comparison rather
 * than a list of equalities that grows a case every time the chain does.
 */
const CHAIN: readonly Objective[] = [
  Objective.WALKING,
  Objective.CARRYING_BAG,
  Objective.GO_HOME,
  Objective.WON,
];

/** Has the run reached `objective`, or anything after it? */
function reached(facts: TutorialFacts, objective: Objective): boolean {
  return CHAIN.indexOf(facts.objective) >= CHAIN.indexOf(objective);
}

/**
 * Has the urge arrived? True from the first sniff onward — including after the
 * dog has been and been cleaned up, because the question this answers is "is
 * the walk past the free-roam opening", not "is the dog sniffing right now".
 */
function urgeStarted(facts: TutorialFacts): boolean {
  return (
    facts.dogState === DogState.SNIFFING ||
    facts.dogState === DogState.NEEDS_GRASS ||
    wentOnGrass(facts)
  );
}

/** Has the dog actually gone, or got as far as squatting to? */
function wentOnGrass(facts: TutorialFacts): boolean {
  return (
    facts.dogState === DogState.POOPING ||
    facts.dogState === DogState.POOP_PENDING ||
    facts.dogState === DogState.CLEANED ||
    reached(facts, Objective.CARRYING_BAG)
  );
}

/**
 * The script.
 *
 * Read the `done` column as the promise each card makes: nothing here asks for
 * anything the walk does not already require, which is what stops the tutorial
 * from being a second game played on top of the first.
 */
export const TUTORIAL_STEPS: readonly Lesson[] = [
  // What the walk is for. A briefing: there is nothing to do yet, and a card
  // that vanished the moment somebody touched a key would never be read.
  { id: 'intro', stale: urgeStarted },
  // Move at all. (Nothing here needs `|| urgeStarted` on top: a dog that needs
  // the grass makes this lesson stale, and stale ends a lesson outright.)
  { id: 'walk', done: (f) => f.walkedPx >= TUTORIAL.WALK_PX, stale: urgeStarted },
  // Keep walking — far enough to feel the lead go taut and drag them off line
  // at least once, which is the mechanic the whole game is built on.
  { id: 'lead', done: (f) => f.walkedPx >= TUTORIAL.LEAD_PX, stale: urgeStarted },
  // What the wildlife does to a dog, with one brought in to look at: the frenzy
  // meter is the one thing on screen a new player has no way to interpret, and
  // a briefing about critters with no critter in sight explains nothing.
  { id: 'critters', stale: urgeStarted, onShow: { type: 'release-critter' } },
  // Watch the dog. The urge has been held back until now (see `holdsUrge`);
  // hurrying it here is what turns "wait and see" into a lesson.
  { id: 'urge', done: urgeStarted, stale: wentOnGrass, onShow: { type: 'hurry-urge' } },
  // Get it onto the grass, which is the only place it will go — and stay for
  // the squat, since bagging it up is the back half of the same lesson rather
  // than a card of its own. It used to be one: "stand by the dog and hold the
  // control until the meter fills", for a control that no longer exists.
  {
    id: 'grass',
    done: (f) => reached(f, Objective.CARRYING_BAG),
    stale: (f) => reached(f, Objective.GO_HOME),
  },
  // The errand, and the crossing that comes with it. A car is sent down the
  // street as they approach it, so the one thing that can end a walk outright
  // is met once, with a horn, on the level that explains it.
  {
    id: 'bin',
    done: (f) => reached(f, Objective.GO_HOME),
    stale: (f) => reached(f, Objective.WON),
    when: { is: (f) => f.nearRoad, effect: { type: 'release-vehicle' } },
  },
  // Nothing comes after winning, so nothing can overtake this one.
  { id: 'home', done: (f) => reached(f, Objective.WON) },
];

/** Where the urge lesson sits, which is where the dog stops being held back. */
const URGE_STEP = TUTORIAL_STEPS.findIndex((s) => s.id === 'urge');

/**
 * The asides, in the order they are offered.
 *
 * Traffic first: it is the one that can end the run in the next second, so if a
 * car and a critter arrive together the car is what the card says.
 */
export const TUTORIAL_TIPS: readonly {
  readonly id: TutorialTipId;
  when(facts: TutorialFacts): boolean;
}[] = [
  { id: 'traffic', when: (f) => f.hornSounding },
  { id: 'critter', when: (f) => f.frenzy > TUTORIAL.CRITTER_TIP_AT },
];

/** A guided walk, at the first lesson. */
export function initialTutorial(): TutorialState {
  return { index: 0, shownMs: 0, tip: null, tipMs: 0, seenTips: [], spentEffects: [] };
}

/**
 * Should the dog's urge be held off?
 *
 * True until the script reaches the lesson about it. A dog that needs the grass
 * while the player is still being told what the lead does turns four lessons
 * into cards that flash past — and on the one level whose whole job is to
 * explain the game, waiting a few seconds for the player is the right way round.
 *
 * A **query rather than an effect**, because it is the answer to "what should
 * the dog do this frame" rather than a thing to be done once, and `Game` has to
 * ask it before stepping the dog rather than after.
 */
export function holdsUrge(state: TutorialState): boolean {
  return state.index < URGE_STEP;
}

/**
 * Advance the script one frame.
 *
 * Several lessons can complete in one call, but only when the walk has genuinely
 * overtaken them — see the note on `MIN_SHOW_MS` up top.
 */
export function stepTutorial(
  state: TutorialState,
  facts: TutorialFacts,
  deltaMs: number,
): TutorialStep {
  let index = state.index;
  let shownMs = state.shownMs + deltaMs;
  let spentEffects = state.spentEffects;
  const effects: TutorialEffect[] = [];

  /** Give a direction once per run, whoever asks for it. */
  const give = (effect: TutorialEffect | undefined): void => {
    if (!effect || spentEffects.includes(effect.type)) return;
    effects.push(effect);
    spentEffects = [...spentEffects, effect.type];
  };

  while (index < TUTORIAL_STEPS.length) {
    const lesson = TUTORIAL_STEPS[index];
    // Left behind ends a lesson outright — it is over whether or not it was
    // done, and whether or not it has been up long enough to read. A briefing
    // about the lead is exactly the card that has to go when the dog starts
    // casting about for a park.
    const stale = lesson.stale?.(facts) ?? false;
    // A briefing has no `done`: the clock finishes it, which is the whole
    // difference between explaining something and asking for it.
    const finished = lesson.done ? lesson.done(facts) : shownMs >= TUTORIAL.BRIEF_MS;
    if (!stale && !finished) break;
    if (!stale && shownMs < TUTORIAL.MIN_SHOW_MS) break;
    index++;
    shownMs = 0;
  }

  const current = TUTORIAL_STEPS[index];
  if (current) {
    give(current.onShow);
    if (current.when?.is(facts)) give(current.when.effect);
  }

  let { tip, tipMs } = state;
  let seenTips = state.seenTips;

  if (tip !== null) {
    tipMs -= deltaMs;
    if (tipMs <= 0) {
      tip = null;
      tipMs = 0;
    }
  }
  // Only while there is a card to put it on: an aside with no lesson under it
  // would pop the whole panel back up after the guided walk had finished.
  if (tip === null && current) {
    const fired = TUTORIAL_TIPS.find((t) => !seenTips.includes(t.id) && t.when(facts));
    if (fired) {
      tip = fired.id;
      tipMs = TUTORIAL.TIP_MS;
      seenTips = [...seenTips, fired.id];
    }
  }

  return [{ index, shownMs, tip, tipMs, seenTips, spentEffects }, effects];
}

/**
 * Put the script away.
 *
 * The last lesson is finished by winning, and a won run stops being simulated —
 * so without this the card would sit under the end-of-run overlay saying "walk
 * back to your front door" on a walk that had ended. `Game` calls it on both
 * endings, which is also what takes the tutorial off a run that ended badly.
 */
export function finishTutorial(state: TutorialState): TutorialState {
  return { ...state, index: TUTORIAL_STEPS.length, tip: null, tipMs: 0 };
}

/** What to draw, or `null` once the guided walk is over. */
export function tutorialCard(state: TutorialState): TutorialCard | null {
  const step = TUTORIAL_STEPS[state.index];
  if (!step) return null;
  return {
    step: step.id,
    number: state.index + 1,
    total: TUTORIAL_STEPS.length,
    tip: state.tip,
  };
}

/** Whether two cards would draw the same thing. What `Game` edge-triggers on. */
export function sameCard(a: TutorialCard | null, b: TutorialCard | null): boolean {
  if (a === null || b === null) return a === b;
  return a.step === b.step && a.tip === b.tip;
}
